import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { BUSINESS_NAME } from "@/lib/brand";

export default function Regulatory() {
  return (
    <div>
      <Helmet>
        <title>Regulatory Information — Reliserve Wealth</title>
        <meta
          name="description"
          content="Full regulatory and compliance information for Reliserve Wealth — AMFI-registered Mutual Fund Distributor (ARN: 369518), IRDAI Insurance Agent appointments, scheme disclaimers, cost structure, and grievance redressal escalation path."
        />
      </Helmet>

      {/* Hero */}
      <section className="border-b hairline">
        <div className="max-w-4xl mx-auto px-6 md:px-8 pt-14 md:pt-20 pb-10 md:pb-14">
          <div className="eyebrow mb-3">Compliance · Disclosures</div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05]">
            Regulatory Information
          </h1>
          <p className="text-ink-soft text-[15.5px] leading-relaxed mt-5 max-w-2xl">
            The full set of registrations, appointments, and statutory disclaimers under which{" "}
            {BUSINESS_NAME} offers Mutual Fund Distribution and Insurance Agency services.
          </p>
        </div>
      </section>

      {/* Registrations */}
      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-14 md:py-16 space-y-10">
          <Block eyebrow="Mutual Fund Distribution" title="AMFI-registered Mutual Fund Distributor">
            <p>
              <span className="text-ink font-medium">ARN:</span>{" "}
              <span className="num text-ink">369518</span>
            </p>
            <p className="mt-2">
              {BUSINESS_NAME} operates within the regulatory framework laid down by SEBI (Securities
              and Exchange Board of India) and adheres to the Code of Conduct for Mutual Fund
              Distributors prescribed by AMFI (Association of Mutual Funds in India).
            </p>
          </Block>

          <Block eyebrow="Insurance Agency" title="IRDAI-registered Insurance Agent appointments">
            <div className="grid md:grid-cols-2 gap-5 mt-2">
              <AppointmentCard
                tag="Non-Life Insurer"
                insurer="ICICI Lombard General Insurance Company Ltd."
                code="ILG50017"
                date="13 Jun 2019"
              />
              <AppointmentCard
                tag="Life Insurer"
                insurer="Axis Max Life Insurance Co. Ltd."
                code="MAX42874H"
                date="26 Aug 2026"
              />
            </div>
            <p className="mt-5">
              Rohan Ghotage (operating under the trade name {BUSINESS_NAME}) is a licensed insurance
              agent registered with the Insurance Regulatory and Development Authority of India
              (IRDAI).
            </p>
          </Block>
        </div>
      </section>

      {/* Disclaimers */}
      <section className="border-b hairline">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-14 md:py-16 space-y-10">
          <Block eyebrow="Disclaimer · 01" title="Mutual Fund Disclaimer">
            <p>
              Mutual fund investments are subject to market risks. Please read the scheme information
              document and key information memorandum carefully before investing. Past performance is
              not indicative of future returns. {BUSINESS_NAME} is a mutual fund distributor registered
              with AMFI. {BUSINESS_NAME} does not guarantee or assure any returns on investments.
            </p>
          </Block>

          <Block eyebrow="Disclaimer · 02" title="Insurance Disclaimer">
            <p>
              Insurance is the subject matter of solicitation. Rohan Ghotage ({BUSINESS_NAME}) is a
              licensed insurance agent registered with IRDAI. We do not underwrite an insurance policy
              nor can we influence any insurance decision. Please read the policy terms and conditions
              carefully before concluding the sale. The purchase of insurance is voluntary.
            </p>
          </Block>

          <Block eyebrow="Cost structure · Advisory" title="A note on costs, taxes and plan types">
            <p>
              All current and prospective investors are advised to check and evaluate the Exit Loads,
              Taxation, and other cost structures (TER) applicable before making an investment
              decision in mutual fund schemes.
            </p>
            <p className="mt-3">
              At {BUSINESS_NAME}, we only offer{" "}
              <span className="text-ink font-medium">Regular Plans</span> of mutual fund schemes and
              earn a trailing commission on the investment amount.
            </p>
          </Block>
        </div>
      </section>

      {/* Trade name + Grievance redressal */}
      <section className="bg-paper-raised">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-14 md:py-16 space-y-10">
          <Block eyebrow="Entity" title="Trade name">
            <p>
              <span className="text-ink font-medium">{BUSINESS_NAME}</span> is the trade name under
              which <span className="text-ink font-medium">Rohan Ghotage</span> operates.
            </p>
          </Block>

          <Block eyebrow="Grievance redressal" title="How to raise a concern">
            <p>
              If you have a concern about our service, please write to{" "}
              <a href="mailto:grievance@reliservewealth.com" className="link-brass num">
                grievance@reliservewealth.com
              </a>{" "}
              or use our{" "}
              <Link to="/grievance-redressal" className="link-brass">
                Grievance Redressal Form
              </Link>{" "}
              for a tracked response.
            </p>
            <p className="mt-3">
              Unresolved complaints relating to mutual funds may be escalated to AMFI
              (amfiindia.com), and those relating to insurance to IRDAI (irdai.gov.in) or the
              Insurance Ombudsman.
            </p>
          </Block>
        </div>
      </section>
    </div>
  );
}

function Block({ eyebrow, title, children }) {
  return (
    <div data-testid={`regulatory-block-${eyebrow.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="eyebrow mb-2">{eyebrow}</div>
      <h2 className="font-display text-xl md:text-2xl text-ink leading-snug mb-4">{title}</h2>
      <div className="text-[14.5px] leading-relaxed text-ink-soft space-y-0">{children}</div>
    </div>
  );
}

function AppointmentCard({ tag, insurer, code, date }) {
  return (
    <div className="card-paper p-5">
      <div className="eyebrow text-[10.5px] mb-2">{tag}</div>
      <p className="text-ink font-medium text-[15px] leading-snug">{insurer}</p>
      <div className="mt-3 pt-3 border-t hairline text-[12.5px] text-ink-soft space-y-1">
        <p>
          Agency Code: <span className="num text-ink">{code}</span>
        </p>
        <p>
          Date of Appointment: <span className="num text-ink">{date}</span>
        </p>
      </div>
    </div>
  );
}
